/**
 * Account Storage Service for Multi-Account Management
 * Provides secure storage and retrieval of multiple account credentials
 */

import { AuthSecret } from '../../../types/utils/authSecretTypes';
import { logger } from '../../../utils/logger';
import { LoginPersistenceOptions } from './authSecretUtils';
import { 
  EncryptionService, 
  EncryptedData, 
  AccountCredentials, 
  SessionKeyManager 
} from './encryptionService';

export interface StoredAccount {
  id: string;
  loginName: string;
  userName: string;
  email: string;
  profilePicture?: string;
  encryptedCredentials: string;
  lastUsed: number;
  rememberMe: boolean;
  persistenceDays: number;
}

export interface AccountStorage {
  saveAccount(account: StoredAccount): Promise<void>;
  getAccount(accountId: string): Promise<StoredAccount | null>;
  getAllAccounts(): Promise<StoredAccount[]>;
  removeAccount(accountId: string): Promise<void>;
  updateLastUsed(accountId: string): Promise<void>;
  clearAllAccounts(): Promise<void>;
  getActiveAccountId(): string | null;
  setActiveAccountId(accountId: string): void;
  clearActiveAccount(): void;
}

/**
 * Multi-account storage constants
 */
export const MULTI_ACCOUNT_STORAGE = {
  ACCOUNTS_LIST_KEY: 'smartmatrix_accounts_list',
  ACTIVE_ACCOUNT_KEY: 'smartmatrix_active_account',
  ACCOUNT_PREFIX: 'smartmatrix_account_',
  STORAGE_VERSION: '1.0'
} as const;

/**
 * Local storage implementation of AccountStorage
 */
export class LocalAccountStorage implements AccountStorage {
  
  /**
   * Save account to storage
   */
  async saveAccount(account: StoredAccount): Promise<void> {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        logger.warn('Cannot save account in server environment');
        return;
      }
      
      // Validate encryption service availability
      if (!EncryptionService.isSupported()) {
        throw new Error('Encryption not supported in this environment');
      }

      // Get or create accounts list
      const accountsList = await this.getAccountsList();
      
      // Add account ID to list if not already present
      if (!accountsList.includes(account.id)) {
        accountsList.push(account.id);
        this.saveAccountsList(accountsList);
      }

      // Determine storage type based on rememberMe setting
      const storage = account.rememberMe ? localStorage : sessionStorage;
      
      // Store account data
      const accountKey = MULTI_ACCOUNT_STORAGE.ACCOUNT_PREFIX + account.id;
      storage.setItem(accountKey, JSON.stringify(account));

      logger.auth(`Account ${account.id} saved to ${account.rememberMe ? 'localStorage' : 'sessionStorage'}`);
    } catch (error) {
      logger.error('Failed to save account', error, { accountId: account.id });
      throw new Error('Failed to save account to storage');
    }
  }

  /**
   * Get account from storage
   */
  async getAccount(accountId: string): Promise<StoredAccount | null> {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        return null;
      }
      
      const accountKey = MULTI_ACCOUNT_STORAGE.ACCOUNT_PREFIX + accountId;
      
      // Try localStorage first, then sessionStorage
      let accountData = localStorage.getItem(accountKey);
      
      if (!accountData) {
        accountData = sessionStorage.getItem(accountKey);
      }

      if (!accountData) {
        return null;
      }

      const account = JSON.parse(accountData) as StoredAccount;
      
      // Validate account structure
      if (!this.validateStoredAccount(account)) {
        logger.warn(`Invalid account structure for ${accountId}, removing`);
        await this.removeAccount(accountId);
        return null;
      }

      return account;
    } catch (error) {
      console.error(`Failed to get account ${accountId}:`, error);
      return null;
    }
  }

  /**
   * Get all accounts from storage
   */
  async getAllAccounts(): Promise<StoredAccount[]> {
    try {
      const accountsList = await this.getAccountsList();
      const accounts: StoredAccount[] = [];

      for (const accountId of accountsList) {
        const account = await this.getAccount(accountId);
        if (account) {
          accounts.push(account);
        } else {
          logger.warn(`Account ${accountId} in list but not found in storage - cleaning up`);
          // Remove from accounts list if account data is missing
          const updatedList = accountsList.filter(id => id !== accountId);
          this.saveAccountsList(updatedList);
        }
      }
      
      // Keep original insertion order - DO NOT sort by lastUsed
      // This ensures the account list doesn't re-order when switching accounts
      return accounts;
    } catch (error) {
      console.error('Failed to get all accounts:', error);
      return [];
    }
  }

  /**
   * Remove account from storage
   */
  async removeAccount(accountId: string): Promise<void> {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        console.warn('Cannot remove account in server environment');
        return;
      }
      
      const accountKey = MULTI_ACCOUNT_STORAGE.ACCOUNT_PREFIX + accountId;
      
      // Remove from both storage types
      localStorage.removeItem(accountKey);
      sessionStorage.removeItem(accountKey);

      // Update accounts list
      const accountsList = await this.getAccountsList();
      const updatedList = accountsList.filter(id => id !== accountId);
      this.saveAccountsList(updatedList);

      // Clear active account if it was the removed one
      const activeAccountId = this.getActiveAccountId();
      if (activeAccountId === accountId) {
        this.clearActiveAccount();
        
        // Clear metadata from storage when removing active account
        const { MetadataSyncService } = await import('./metadataSync');
        MetadataSyncService.clearMetadataFromStorage();
        console.log(`Account ${accountId} was active - metadata cleared from storage`);
      }

      console.log(`Account ${accountId} removed from storage`);
    } catch (error) {
      console.error(`Failed to remove account ${accountId}:`, error);
      throw new Error('Failed to remove account from storage');
    }
  }

  /**
   * Update last used timestamp for account
   */
  async updateLastUsed(accountId: string): Promise<void> {
    try {
      console.log('🔥 [accountStorage.updateLastUsed] Called with:', { accountId });
      const account = await this.getAccount(accountId);
      if (!account) {
        throw new Error(`Account ${accountId} not found`);
      }

      console.log('🔥 [accountStorage.updateLastUsed] Found account:', {
        accountId: account.id,
        loginName: account.loginName,
        email: account.email
      });

      account.lastUsed = Date.now();
      await this.saveAccount(account);
    } catch (error) {
      console.error(`Failed to update last used for account ${accountId}:`, error);
      throw new Error('Failed to update account last used timestamp');
    }
  }

  /**
   * Clear all accounts from storage with robust session handling
   */
  async clearAllAccounts(): Promise<void> {
    try {
      const accountsList = await this.getAccountsList();
      
      // Remove all account data
      for (const accountId of accountsList) {
        const accountKey = MULTI_ACCOUNT_STORAGE.ACCOUNT_PREFIX + accountId;
        localStorage.removeItem(accountKey);
        sessionStorage.removeItem(accountKey);
      }

      // Clear accounts list and active account
      this.saveAccountsList([]);
      this.clearActiveAccount();

      // Permanently clear encryption session when removing all accounts
      // This ensures a fresh encryption session for the next login
      try {
        const { SessionKeyManager } = await import('./encryptionService');
        SessionKeyManager.clearSessionPermanently();
        console.log('Encryption session permanently cleared with all accounts');
      } catch (error) {
        console.warn('Failed to permanently clear encryption session:', error);
      }

      console.log('All accounts cleared from storage');
    } catch (error) {
      console.error('Failed to clear all accounts:', error);
      throw new Error('Failed to clear all accounts from storage');
    }
  }

  /**
   * Get accounts list from storage
   */
  private async getAccountsList(): Promise<string[]> {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        return [];
      }
      
      const listData = localStorage.getItem(MULTI_ACCOUNT_STORAGE.ACCOUNTS_LIST_KEY) ||
                      sessionStorage.getItem(MULTI_ACCOUNT_STORAGE.ACCOUNTS_LIST_KEY);
      
      if (!listData) {
        return [];
      }

      const accountsList = JSON.parse(listData) as string[];
      return Array.isArray(accountsList) ? accountsList : [];
    } catch (error) {
      console.error('Failed to get accounts list:', error);
      return [];
    }
  }

  /**
   * Save accounts list to storage
   */
  private saveAccountsList(accountsList: string[]): void {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return;
    }
    
    const listData = JSON.stringify(accountsList);
    
    // Save to localStorage for persistence
    localStorage.setItem(MULTI_ACCOUNT_STORAGE.ACCOUNTS_LIST_KEY, listData);
    
    // Also save to sessionStorage for current session access
    sessionStorage.setItem(MULTI_ACCOUNT_STORAGE.ACCOUNTS_LIST_KEY, listData);
  }

  /**
   * Get active account ID
   */
  getActiveAccountId(): string | null {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return null;
    }
    
    return localStorage.getItem(MULTI_ACCOUNT_STORAGE.ACTIVE_ACCOUNT_KEY) ||
           sessionStorage.getItem(MULTI_ACCOUNT_STORAGE.ACTIVE_ACCOUNT_KEY);
  }

  /**
   * Set active account ID
   */
  setActiveAccountId(accountId: string): void {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return;
    }
    
    localStorage.setItem(MULTI_ACCOUNT_STORAGE.ACTIVE_ACCOUNT_KEY, accountId);
    sessionStorage.setItem(MULTI_ACCOUNT_STORAGE.ACTIVE_ACCOUNT_KEY, accountId);
  }

  /**
   * Clear active account
   */
  clearActiveAccount(): void {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return;
    }
    
    localStorage.removeItem(MULTI_ACCOUNT_STORAGE.ACTIVE_ACCOUNT_KEY);
    sessionStorage.removeItem(MULTI_ACCOUNT_STORAGE.ACTIVE_ACCOUNT_KEY);
  }

  /**
   * Validate stored account structure
   */
  private validateStoredAccount(account: any): account is StoredAccount {
    return (
      account &&
      typeof account === 'object' &&
      typeof account.id === 'string' &&
      typeof account.loginName === 'string' &&
      typeof account.userName === 'string' &&
      typeof account.email === 'string' &&
      typeof account.encryptedCredentials === 'string' &&
      typeof account.lastUsed === 'number' &&
      typeof account.rememberMe === 'boolean' &&
      typeof account.persistenceDays === 'number'
    );
  }
}

/**
 * Account credentials helper functions
 */
export class AccountCredentialsHelper {
  
  /**
   * Create StoredAccount from AuthSecret and options
   */
  static async createStoredAccount(
    authSecret: AuthSecret,
    options: LoginPersistenceOptions,
    email?: string,
    profilePicture?: string
  ): Promise<StoredAccount> {
    try {
      // Generate unique account ID
      const accountId = this.generateAccountId(authSecret.loginName, email || authSecret.loginName);
      
      const currentTime = Date.now();
      
      // Prepare credentials for encryption
      const credentials: AccountCredentials = {
        authSecret,
        email: email || authSecret.loginName,
        profilePicture,
        lastUsed: currentTime,
        rememberMe: options.rememberMe,
        persistenceDays: options.persistenceDays || 1,
        loginTimestamp: currentTime,
        lastActivityTimestamp: currentTime
      };

      // Encrypt credentials
      const sessionId = SessionKeyManager.getSessionId();
      const encryptedData = await EncryptionService.encrypt(credentials, sessionId);

      const storedAccount = {
        id: accountId,
        loginName: authSecret.loginName,
        userName: authSecret.userName,
        email: email || authSecret.loginName,
        profilePicture,
        encryptedCredentials: JSON.stringify(encryptedData),
        lastUsed: currentTime,
        rememberMe: options.rememberMe,
        persistenceDays: options.persistenceDays || 1
      };

      // Sync metadata to storage
      const { MetadataSyncService } = await import('./metadataSync');
      const storage = options.rememberMe ? localStorage : sessionStorage;
      MetadataSyncService.syncFromCredentials(credentials, storage);

      return storedAccount;
    } catch (error) {
      console.error('Failed to create stored account:', error);
      throw new Error('Failed to create stored account');
    }
  }

  /**
   * Extract AuthSecret from StoredAccount with robust session key handling
   */
  static async extractAuthSecret(account: StoredAccount): Promise<AuthSecret> {
    try {
      const encryptedData = JSON.parse(account.encryptedCredentials) as EncryptedData;
      const sessionId = SessionKeyManager.getSessionId();
      const credentials = await EncryptionService.decrypt(encryptedData, sessionId);
      
      // Sync metadata to storage for validation
      const { MetadataSyncService } = await import('./metadataSync');
      const storage = account.rememberMe ? localStorage : sessionStorage;
      MetadataSyncService.syncFromCredentials(credentials, storage);
      
      return credentials.authSecret;
    } catch (error) {
      console.error('Failed to extract auth secret for account:', account.id, error);
      
      // Instead of trying to recover with session key regeneration (which creates more problems),
      // remove the corrupted account and let the user re-login cleanly
      console.log('Removing corrupted account that cannot be decrypted:', account.id);
      
      // Remove the corrupted account from storage
      try {
        const storage = new LocalAccountStorage();
        await storage.removeAccount(account.id);
        console.log(`Corrupted account ${account.id} removed from storage`);
      } catch (removeError) {
        console.warn('Failed to remove corrupted account:', removeError);
      }
      
      throw new Error(`Account credentials corrupted for ${account.loginName}. Account removed. Please log in again.`);
    }
  }

  /**
   * Migrate existing account to include metadata
   */
  static async migrateAccountMetadata(account: StoredAccount): Promise<void> {
    try {
      // Decrypt credentials
      const encryptedData = JSON.parse(account.encryptedCredentials) as EncryptedData;
      const sessionId = SessionKeyManager.getSessionId();
      const credentials = await EncryptionService.decrypt(encryptedData, sessionId);

      // Check if metadata is missing
      let needsUpdate = false;
      
      if (!credentials.loginTimestamp) {
        credentials.loginTimestamp = account.lastUsed;
        needsUpdate = true;
      }
      
      if (!credentials.lastActivityTimestamp) {
        credentials.lastActivityTimestamp = account.lastUsed;
        needsUpdate = true;
      }

      if (needsUpdate) {
        // Re-encrypt and save
        const newEncryptedData = await EncryptionService.encrypt(credentials, sessionId);
        account.encryptedCredentials = JSON.stringify(newEncryptedData);
        await accountStorage.saveAccount(account);

        // Sync to storage
        const { MetadataSyncService } = await import('./metadataSync');
        const storage = account.rememberMe ? localStorage : sessionStorage;
        MetadataSyncService.syncFromCredentials(credentials, storage);
      }
    } catch (error) {
      console.error(`AccountCredentialsHelper: Failed to migrate metadata for account ${account.id}:`, error);
      throw new Error('Failed to migrate account metadata');
    }
  }

  /**
   * Generate unique account ID
   */
  private static generateAccountId(loginName: string, email: string): string {
    // Use email as primary identifier, fallback to loginName
    const identifier = email || loginName;
    
    // Create a simple hash of the identifier
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      const char = identifier.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to positive number and add timestamp for uniqueness
    const hashStr = Math.abs(hash).toString(36);
    const timestamp = Date.now().toString(36);
    
    return `acc_${hashStr}_${timestamp}`;
  }

  /**
   * Check if account with email or loginName already exists
   */
  static async isDuplicateAccount(email: string, storage: AccountStorage, loginName?: string): Promise<boolean> {
    try {
      const accounts = await storage.getAllAccounts();
      
      // Check for duplicates using both email and loginName (case-insensitive)
      return accounts.some(account => {
        const emailMatch = account.email && email && 
          account.email.toLowerCase().trim() === email.toLowerCase().trim();
        
        const loginNameMatch = loginName && account.loginName && 
          account.loginName.toLowerCase().trim() === loginName.toLowerCase().trim();
        
        // Also check if loginName matches email field (common case)
        const crossMatch = loginName && account.email && 
          account.email.toLowerCase().trim() === loginName.toLowerCase().trim();
        
        return emailMatch || loginNameMatch || crossMatch;
      });
    } catch (error) {
      console.error('Failed to check for duplicate account:', error);
      return false;
    }
  }

  /**
   * Find existing account by email or loginName
   */
  static async findExistingAccount(email: string, storage: AccountStorage, loginName?: string): Promise<StoredAccount | null> {
    try {
      const accounts = await storage.getAllAccounts();
      
      // Find account using both email and loginName (case-insensitive)
      return accounts.find(account => {
        const emailMatch = account.email && email && 
          account.email.toLowerCase().trim() === email.toLowerCase().trim();
        
        const loginNameMatch = loginName && account.loginName && 
          account.loginName.toLowerCase().trim() === loginName.toLowerCase().trim();
        
        // Also check if loginName matches email field (common case)
        const crossMatch = loginName && account.email && 
          account.email.toLowerCase().trim() === loginName.toLowerCase().trim();
        
        return emailMatch || loginNameMatch || crossMatch;
      }) || null;
    } catch (error) {
      console.error('Failed to find existing account:', error);
      return null;
    }
  }

  /**
   * Clean up duplicate accounts and merge them
   */
  static async cleanupDuplicateAccounts(storage: AccountStorage): Promise<{
    duplicatesFound: number;
    duplicatesRemoved: number;
    errors: string[];
  }> {
    const result = {
      duplicatesFound: 0,
      duplicatesRemoved: 0,
      errors: [] as string[]
    };

    try {
      const accounts = await storage.getAllAccounts();

      // Group accounts by normalized email/loginName
      const accountGroups = new Map<string, StoredAccount[]>();
      
      for (const account of accounts) {
        // Create a normalized key for grouping
        const normalizedEmail = account.email?.toLowerCase().trim() || '';
        const normalizedLoginName = account.loginName?.toLowerCase().trim() || '';
        
        // Use email as primary key, fallback to loginName
        const groupKey = normalizedEmail || normalizedLoginName;
        
        if (groupKey) {
          if (!accountGroups.has(groupKey)) {
            accountGroups.set(groupKey, []);
          }
          accountGroups.get(groupKey)!.push(account);
        }
      }

      // Process groups with duplicates
      for (const [, groupAccounts] of accountGroups) {
        if (groupAccounts.length > 1) {
          result.duplicatesFound += groupAccounts.length - 1;

          // Sort by lastUsed (most recent first)
          groupAccounts.sort((a, b) => b.lastUsed - a.lastUsed);
          
          // Keep the most recently used account
          const removeAccounts = groupAccounts.slice(1);

          // Remove duplicate accounts
          for (const removeAccount of removeAccounts) {
            try {
              await storage.removeAccount(removeAccount.id);
              result.duplicatesRemoved++;
            } catch (error) {
              const errorMsg = `Failed to remove duplicate account ${removeAccount.id}: ${error}`;
              console.error(errorMsg);
              result.errors.push(errorMsg);
            }
          }
        }
      }

      return result;
    } catch (error) {
      const errorMsg = `Failed to cleanup duplicate accounts: ${error}`;
      console.error(errorMsg);
      result.errors.push(errorMsg);
      return result;
    }
  }
}

/**
 * Default account storage instance
 */
export const accountStorage = new LocalAccountStorage();